import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import { useComplaintModal } from "../context/ComplaintModalContext";

// ---- mini-gráficos (SVG/CSS, sem biblioteca) ----
function Donut({ value, cor = "#0077B6", sub }) {
  const r = 52, c = 2 * Math.PI * r;
  return (
    <div className="flex flex-col items-center">
      <svg viewBox="0 0 120 120" className="w-28 h-28">
        <circle cx="60" cy="60" r={r} fill="none" stroke="#e5e7eb" strokeWidth="13" />
        <circle cx="60" cy="60" r={r} fill="none" stroke={cor} strokeWidth="13" strokeLinecap="round"
          strokeDasharray={`${(c * value) / 100} ${c}`} transform="rotate(-90 60 60)" />
        <text x="60" y="60" textAnchor="middle" dominantBaseline="central" fontSize="27" fontWeight="800" fill="#1D3557">{value}%</text>
      </svg>
      {sub && <p className="text-sm text-gray-600 text-center mt-1 max-w-[180px]">{sub}</p>}
    </div>
  );
}
function Bar({ label, value, cor = "#0077B6" }) {
  return (
    <div>
      <div className="flex justify-between text-sm text-gray-700"><span>{label}</span><span className="font-bold">{value}%</span></div>
      <div className="h-3.5 rounded-full bg-gray-200 mt-1 overflow-hidden">
        <div className="h-3.5 rounded-full transition-all" style={{ width: `${value}%`, background: cor }} />
      </div>
    </div>
  );
}
function Stat({ n, l, cor = "text-primary" }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 text-center shadow-sm">
      <div className={`text-4xl md:text-5xl font-extrabold ${cor}`}>{n}</div>
      <div className="text-sm text-gray-600 mt-1">{l}</div>
    </div>
  );
}

export default function PorQuePage() {
  const { openComplaint } = useComplaintModal();
  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <section className="bg-brand text-white">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-12 md:py-16 text-center">
          <span className="inline-block text-4xl">👻</span>
          <h1 className="text-3xl md:text-5xl font-extrabold mt-3">O "vácuo" no recrutamento virou epidemia.</h1>
          <p className="mt-4 text-lg text-white/90 max-w-2xl mx-auto">
            Você fez entrevista, foi bem, e… silêncio. Não é só com você — e cada relato no eaiRH ajuda o próximo candidato e cobra as empresas.
          </p>
          <div className="mt-7 flex flex-wrap gap-3 justify-center">
            <button type="button" onClick={() => openComplaint()} className="rounded-xl bg-orange-600 hover:bg-orange-700 px-6 py-3 font-semibold">Registrar meu relato</button>
            <Link to="/empresas" className="rounded-xl bg-white/15 hover:bg-white/25 px-6 py-3 font-semibold">Ver reputação de uma empresa</Link>
          </div>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-4 md:px-6 py-12 space-y-16">
        {/* O problema */}
        <section>
          <h2 className="text-2xl md:text-3xl font-bold text-text text-center">O problema é gigante — e está crescendo</h2>
          {/* 📚 A FONTE COLADA NO NÚMERO (19/09/2026). Ela só existia no rodapé, e uma IA leu estes números
              perto do nome eaiRH e os apresentou como "dados levantados pela própria plataforma". Não são:
              são pesquisas internacionais. Dado do eaiRH e dado de mercado precisam se distinguir sozinhos. */}
          <p className="text-xs text-gray-500 text-center mt-1">Pesquisas internacionais de experiência do candidato — fontes no fim da página.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
            <Stat n="61%" l="foram deixados no vácuo após uma entrevista" cor="text-danger" />
            <Stat n="80%" l="dos gestores admitem ter dado vácuo em candidatos" cor="text-danger" />
            <Stat n="43%" l="nunca mais se candidatam após uma experiência ruim" cor="text-accent" />
            <Stat n="83%" l="pesquisam a reputação da empresa antes de se candidatar" cor="text-primary" />
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 items-center rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex justify-around">
              <Donut value={94} cor="#0077B6" sub="querem feedback do processo" />
              <Donut value={41} cor="#E63946" sub="realmente recebem feedback" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-lg">O abismo do feedback</h3>
              <p className="text-gray-600 text-sm mt-2">
                Quase todo mundo quer um retorno — pouquíssimos recebem. Esse silêncio é exatamente o que o eaiRH mede, expõe e cobra.
                Empresas que dão retorno têm o selo 💬; as que somem ganham o selo 👻.
              </p>
            </div>
          </div>
        </section>

        {/* Para o candidato */}
        <section>
          <h2 className="text-2xl md:text-3xl font-bold text-text text-center">Por que o SEU relato importa</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            {[
              ["🔎", "Avisa o próximo", "83% pesquisam a empresa antes de aplicar. Seu relato vira informação que evita que outros percam tempo."],
              ["⚖️", "Cobra a empresa", "Reputação pública pressiona o RH a responder. Talento é 4x mais propenso a reconsiderar empresas que dão retorno."],
              ["📊", "Vira dado, não desabafo", "Cada relato alimenta a reputação por taxa (não por volume), com selos de comportamento de retorno."],
            ].map(([i, t, d]) => (
              <div key={t} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="text-3xl">{i}</div>
                <h3 className="font-bold text-gray-900 mt-2">{t}</h3>
                <p className="text-sm text-gray-600 mt-1">{d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Vale a pena? — tratamento de objeções */}
        <section>
          <h2 className="text-2xl md:text-3xl font-bold text-text text-center">"Mas vale a pena registrar?"</h2>
          <p className="text-gray-600 text-center mt-2 max-w-2xl mx-auto">As dúvidas mais comuns — respondidas.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            {[
              ["🤷", "\"Acho que não vai dar em nada\"", "Dá. A reputação é pública e pressiona o RH — e a empresa tem direito de resposta pelo portal. Quanto mais relatos, mais claro fica o padrão de cada empresa."],
              ["🛡️", "\"Tenho medo de represália\"", "Você pode relatar de forma anônima. E nada de contato direto: tudo passa pelo eaiRH, com seu contato protegido (a empresa nunca vê seu e-mail/telefone)."],
              ["⏱️", "\"Dá trabalho?\"", "Leva 2 minutos. Conta a empresa, o que houve e o tipo de retorno. Se quiser, anexa um print (privado) como evidência."],
              ["🕰️", "\"Já passou faz tempo\"", "Relatos antigos contam! Você informa a data do processo, e ele entra no histórico da empresa com a data real do ocorrido."],
            ].map(([i, t, d]) => (
              <div key={t} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">{i}</span>
                  <div>
                    <h3 className="font-bold text-gray-900">{t}</h3>
                    <p className="text-sm text-gray-600 mt-1">{d}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Efeito dominó */}
        <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-xl md:text-2xl font-bold text-text text-center">O efeito dominó de um vácuo</h2>
          <p className="text-gray-600 text-center text-sm mt-1">Um silêncio no recrutamento não para no candidato — vira prejuízo de marca.</p>
          <p className="text-xs text-gray-400 text-center mt-1">Números de pesquisas internacionais (Talent Board, Glassdoor e outras) — fontes no fim da página.</p>
          <div className="mt-6 flex flex-col md:flex-row items-stretch justify-center gap-2">
            {[
              ["👻", "Candidato no vácuo", ""],
              ["🗣️", "72% contam a amigos", "boca a boca"],
              ["📱", "69% postam nas redes", "alcance público"],
              ["🛒", "56% deixam de comprar", "perda de vendas"],
              ["📉", "Até 5% de queda nas ações", "(empresas de capital aberto)"],
            ].map(([i, t, s], idx, arr) => (
              <React.Fragment key={t}>
                <div className="flex-1 rounded-xl bg-gray-50 border border-gray-100 p-3 text-center">
                  <div className="text-2xl">{i}</div>
                  <div className="text-sm font-semibold text-gray-800 mt-1">{t}</div>
                  {s && <div className="text-[11px] text-gray-500">{s}</div>}
                </div>
                {idx < arr.length - 1 && <div className="hidden md:flex items-center text-gray-300 font-bold">→</div>}
              </React.Fragment>
            ))}
          </div>
          <p className="text-xs text-gray-400 text-center mt-3">
            Segundo pesquisas de mercado, 96% das empresas reconhecem que a reputação impacta a receita — mas só 44% monitoram. O eaiRH dá a elas (e a você) esse termômetro.
          </p>
        </section>

        {/* Para a empresa — B2B */}
        <section className="rounded-2xl bg-text text-white p-6 md:p-8">
          <h2 className="text-2xl md:text-3xl font-bold text-center">Para a empresa: o vácuo custa caro</h2>
          <p className="text-white/80 text-center mt-2 max-w-2xl mx-auto">
            Dar vácuo não machuca só o RH — machuca a <strong>marca de consumo e as vendas</strong>.
          </p>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
            <div className="space-y-3 bg-white/5 rounded-xl p-5">
              <Bar label="Deixam de COMPRAR da empresa após experiência ruim" value={56} cor="#FB8500" />
              <Bar label="Contam para amigos, colegas e família" value={72} cor="#FB8500" />
              <Bar label="Compartilham a experiência nas redes sociais" value={69} cor="#FB8500" />
              <Bar label="Empresas que admitem: a reputação impacta a receita" value={96} cor="#48CAE4" />
              <p className="text-[11px] text-white/50">Pesquisas internacionais de experiência do candidato — fontes no fim da página.</p>
            </div>
            <div className="text-center">
              <div className="text-6xl font-extrabold text-orange-400">56%</div>
              <p className="text-white/90 mt-2 max-w-sm mx-auto">
                param de comprar da marca depois de um recrutamento ruim. É por isso que responder no eaiRH protege a empresa —
                e por que a reputação de retorno vale dinheiro.
              </p>
              <Link to="/indicadores" className="inline-block mt-4 rounded-xl bg-white/15 hover:bg-white/25 px-5 py-2.5 font-semibold">Ver os indicadores</Link>
            </div>
          </div>
        </section>

        {/* Como o eaiRH resolve */}
        <section className="text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-text">Como o eaiRH transforma isso</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6 text-left">
            {[
              ["1", "Você relata", "Conta como foi o processo (e o tipo de retorno: 👻 sumiço, 🤖 automático, 💬 personalizado)."],
              ["2", "Vira reputação", "O relato alimenta o score da empresa, calculado por taxas (resposta, solução, satisfação) — volume não mascara qualidade."],
              ["3", "A empresa responde", "Direito de resposta pelo portal. Tudo moderado, sem contato direto."],
              ["4", "Outros decidem", "Quem vai se candidatar vê a reputação antes de aplicar."],
            ].map(([n, t, d]) => (
              <div key={n} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="w-9 h-9 rounded-full bg-primary text-white font-bold flex items-center justify-center">{n}</div>
                <h3 className="font-bold text-gray-900 mt-3">{t}</h3>
                <p className="text-sm text-gray-600 mt-1">{d}</p>
              </div>
            ))}
          </div>
          <button type="button" onClick={() => openComplaint()} className="inline-block mt-8 rounded-xl bg-orange-600 hover:bg-orange-700 text-white px-8 py-3 font-semibold text-lg">Registrar meu relato agora</button>
        </section>

        {/* Fontes */}
        <section className="border-t border-gray-200 pt-6">
          <h3 className="text-sm font-bold text-gray-500 uppercase">Fontes</h3>
          <ul className="mt-2 text-xs text-gray-500 space-y-1 list-disc pl-5">
            <li>Pin — Employer Ghosting Index 2026 · Recruiterflow — Candidate Ghosting</li>
            <li>RecruitBPM · MSH (talentmsh) · JobTwine — Candidate Experience Statistics 2025/2026</li>
            <li>Vouch · Glassdoor — Employer Branding Statistics</li>
            <li>Talent Board — CandE Candidate Experience Benchmark</li>
          </ul>
          <p className="text-xs text-gray-400 mt-2">Estatísticas internacionais de candidate experience/ghosting, usadas para ilustrar a dimensão do problema.</p>
        </section>
      </main>
    </div>
  );
}
